import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { 
  ArrowLeft, 
  Edit, 
  Printer, 
  Download,
  MapPin,
  Calendar,
  Users,
  FileText,
  Plane,
  Hotel,
  Phone,
  Clock,
  CheckCircle,
  XCircle,
  User,
  Building,
  Mail,
  Map,
  Globe,
  Phone as PhoneIcon
} from "lucide-react";
import "./viewItinerary.css";

const API_BASE = "https://crmapi.zauntrip.com/api";

export default function ViewItinerary() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [itinerary, setItinerary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchItinerary();
  }, [id]);

  const fetchItinerary = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/itineraries/${id}`);
      if (!response.ok) throw new Error("Failed to fetch itinerary");
      
      const data = await response.json();
      if (data.success) {
        setItinerary(data.data);
      } else {
        throw new Error(data.message || "Failed to fetch itinerary");
      }
    } catch (error) {
      console.error("Error:", error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "Confirmed": return <CheckCircle size={16} />;
      case "Draft": return <Clock size={16} />;
      case "Cancelled": return <XCircle size={16} />;
      default: return null;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Confirmed": return "status-confirmed";
      case "Draft": return "status-draft";
      case "Cancelled": return "status-cancelled";
      default: return "";
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatTime = (timeString) => {
    if (!timeString) return "";
    return timeString;
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading itinerary...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <h3>Error loading itinerary</h3>
        <p>{error}</p>
        <button onClick={() => navigate(-1)} className="btn-outline">
          <ArrowLeft size={16} /> Go Back
        </button>
      </div>
    );
  }

  if (!itinerary) {
    return (
      <div className="not-found">
        <h3>Itinerary not found</h3>
        <button onClick={() => navigate("/itineraries")} className="btn-primary">
          Back to Itineraries
        </button>
      </div>
    );
  }

  return (
    <div className="view-itinerary">
      {/* Header */}
      <div className="view-header">
        <div className="header-left">
          <button onClick={() => navigate(-1)} className="btn-back">
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1>{itinerary.title}</h1>
            <div className="header-subtitle">
              <span className={`status-badge ${getStatusColor(itinerary.status)}`}>
                {getStatusIcon(itinerary.status)}
                {itinerary.status}
              </span>
              <span className="header-info">
                <Calendar size={14} />
                {formatDate(itinerary.startDate)} - {formatDate(itinerary.endDate)}
                {itinerary.totalDays && ` • ${itinerary.totalDays} days`}
              </span>
            </div>
          </div>
        </div>
        <div className="header-actions">
          <button onClick={() => navigate(`/itineraries/${id}/edit`)} className="btn-primary">
            <Edit size={16} /> Edit Itinerary
          </button>
          <button className="btn-outline">
            <Printer size={16} /> Print
          </button>
          <button className="btn-outline">
            <Download size={16} /> Export
          </button>
        </div>
      </div>

      {/* Overview Card */}
      <div className="overview-card">
        <div className="overview-section">
          <h3><Users size={18} /> Travelers</h3>
          <div className="travelers-list">
            {itinerary.preparedFor && itinerary.preparedFor.length > 0 ? (
              itinerary.preparedFor.map((traveler, index) => (
                <span key={index} className="traveler-tag">{traveler}</span>
              ))
            ) : (
              <p className="no-data">No travelers specified</p>
            )}
          </div>
        </div>

        <div className="overview-section">
          <h3><MapPin size={18} /> Destinations</h3>
          <div className="destinations-grid">
            {itinerary.destinations && itinerary.destinations.length > 0 ? (
              itinerary.destinations.map((dest, index) => (
                <div key={index} className="destination-item">
                  <div className="destination-header">
                    <MapPin size={16} />
                    <strong>{dest.city}, {dest.country}</strong>
                  </div>
                  {dest.description && <p className="destination-desc">{dest.description}</p>}
                </div>
              ))
            ) : (
              <p className="no-data">No destinations specified</p>
            )}
          </div>
        </div>

        {itinerary.notes && (
          <div className="overview-section">
            <h3><FileText size={18} /> Notes</h3>
            <p className="notes-text">{itinerary.notes}</p>
          </div>
        )}
      </div>

      {/* Itinerary Days */}
      <div className="section">
        <h2>Day-by-Day Itinerary</h2>
        <div className="days-container">
          {itinerary.days && itinerary.days.length > 0 ? (
            itinerary.days.map((day, index) => (
              <div key={day._id || index} className="day-card">
                <div className="day-header">
                  <div className="day-number">Day {day.dayNumber || index + 1}</div>
                  <div className="day-info">
                    <span className="day-date">{formatDate(day.date)}</span>
                    {day.city && <span className="day-city"><MapPin size={14} /> {day.city}</span>}
                  </div>
                </div>
                
                <div className="day-content">
                  {day.title && <h4>{day.title}</h4>}
                  {day.description && <p className="day-description">{day.description}</p>}
                  
                  {/* Activities */}
                  {day.activities && day.activities.length > 0 && (
                    <div className="activities-section">
                      <h5>Activities</h5>
                      <div className="activities-list">
                        {day.activities.map((activity, actIndex) => (
                          <div key={actIndex} className="activity-item">
                            <div className="activity-time">
                              {activity.startTime && (
                                <span>{formatTime(activity.startTime)}</span>
                              )}
                              {activity.endTime && (
                                <>
                                  <span>→</span>
                                  <span>{formatTime(activity.endTime)}</span>
                                </>
                              )}
                            </div>
                            <div className="activity-details">
                              <strong>{activity.name}</strong>
                              {activity.description && <p>{activity.description}</p>}
                              {(activity.guideName || activity.guidePhone) && (
                                <div className="guide-info">
                                  <User size={12} />
                                  <span>{activity.guideName}</span>
                                  {activity.guidePhone && <span> • {activity.guidePhone}</span>}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Meals */}
                  {day.mealsIncluded && day.mealsIncluded.length > 0 && (
                    <div className="meals-section">
                      <h5>Meals Included</h5>
                      <div className="meals-tags">
                        {day.mealsIncluded.map((meal, mealIndex) => (
                          <span key={mealIndex} className="meal-tag">{meal}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Inclusions & Exclusions */}
                  {(day.inclusions?.length > 0 || day.exclusions?.length > 0) && (
                    <div className="inclusions-section">
                      {day.inclusions?.length > 0 && (
                        <div className="inclusions-list">
                          <h6>Inclusions:</h6>
                          <ul>
                            {day.inclusions.map((item, idx) => (
                              <li key={idx}>{item}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {day.exclusions?.length > 0 && (
                        <div className="exclusions-list">
                          <h6>Exclusions:</h6>
                          <ul>
                            {day.exclusions.map((item, idx) => (
                              <li key={idx}>{item}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))
          ) : (
            <div className="no-data-card">
              <FileText size={40} />
              <h3>No days planned</h3>
              <p>Add days to this itinerary to see them here.</p>
            </div>
          )}
        </div>
      </div>

      {/* Flights Section */}
      <div className="section">
        <h2><Plane size={20} /> Flights</h2>
        <div className="flights-container">
          {itinerary.flights && itinerary.flights.length > 0 ? (
            itinerary.flights.map((flight, index) => (
              <div key={flight._id || index} className="flight-card">
                <div className="flight-header">
                  <div className="flight-info">
                    <span className="flight-number">{flight.flightNumber}</span>
                    <span className="flight-airline">{flight.airline}</span>
                    {flight.operatedBy && flight.operatedBy !== flight.airline && (
                      <span className="flight-operated">Operated by: {flight.operatedBy}</span>
                    )}
                  </div>
                </div>
                <div className="flight-details">
                  <div className="flight-route">
                    <div className="departure">
                      <div className="city">{flight.fromCity}</div>
                      <div className="datetime">
                        {formatDate(flight.departureDate)} at {formatTime(flight.departureTime)}
                      </div>
                    </div>
                    <div className="flight-arrow">→</div>
                    <div className="arrival">
                      <div className="city">{flight.toCity}</div>
                      <div className="datetime">
                        {formatDate(flight.arrivalDate)} at {formatTime(flight.arrivalTime)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="no-data-card">
              <Plane size={40} />
              <h3>No flights added</h3>
              <p>Add flight details to this itinerary.</p>
            </div>
          )}
        </div>
      </div>

      {/* Hotels Section */}
      <div className="section">
        <h2><Hotel size={20} /> Hotels</h2>
        <div className="hotels-container">
          {itinerary.hotels && itinerary.hotels.length > 0 ? (
            itinerary.hotels.map((hotel, index) => (
              <div key={hotel._id || index} className="hotel-card">
                <div className="hotel-header">
                  <h3>{hotel.name}</h3>
                  <div className="hotel-location">
                    <MapPin size={14} />
                    <span>{hotel.city}, {hotel.country}</span>
                  </div>
                </div>
                <div className="hotel-details">
                  {hotel.address && (
                    <div className="hotel-info">
                      <Map size={14} />
                      <span>{hotel.address}</span>
                    </div>
                  )}
                  {hotel.phone && (
                    <div className="hotel-info">
                      <PhoneIcon size={14} />
                      <span>{hotel.phone}</span>
                    </div>
                  )}
                  
                  {/* Room Details */}
                  {hotel.roomDetails && hotel.roomDetails.length > 0 && (
                    <div className="room-details">
                      <h4>Room Details</h4>
                      <div className="rooms-list">
                        {hotel.roomDetails.map((room, roomIndex) => (
                          <div key={roomIndex} className="room-item">
                            <span className="room-type">{room.roomType}</span>
                            <span className="room-nights">{room.nights} nights</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Images */}
                  {hotel.images && hotel.images.length > 0 && (
                    <div className="hotel-images">
                      <h4>Images</h4>
                      <div className="images-grid">
                        {hotel.images.map((image, imgIndex) => (
                          <div key={imgIndex} className="image-placeholder">
                            <img src={image} alt={`Hotel ${index + 1} - Image ${imgIndex + 1}`} />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))
          ) : (
            <div className="no-data-card">
              <Hotel size={40} />
              <h3>No hotels added</h3>
              <p>Add hotel details to this itinerary.</p>
            </div>
          )}
        </div>
      </div>

      {/* Local Contacts Section */}
      <div className="section">
        <h2><Phone size={20} /> Local Contacts</h2>
        <div className="contacts-container">
          {itinerary.localContacts && itinerary.localContacts.length > 0 ? (
            itinerary.localContacts.map((contact, index) => (
              <div key={contact._id || index} className="contact-card">
                <div className="contact-header">
                  <User size={20} />
                  <div>
                    <h3>{contact.name}</h3>
                    <p className="contact-role">
                      {contact.role}
                      {contact.company && ` at ${contact.company}`}
                    </p>
                  </div>
                </div>
                <div className="contact-details">
                  {contact.phone && (
                    <div className="contact-info">
                      <Phone size={14} />
                      <span>{contact.phone}</span>
                    </div>
                  )}
                  {contact.email && (
                    <div className="contact-info">
                      <Mail size={14} />
                      <span>{contact.email}</span>
                    </div>
                  )}
                  {contact.address && (
                    <div className="contact-info">
                      <Map size={14} />
                      <span>{contact.address}</span>
                    </div>
                  )}
                </div>
              </div>
            ))
          ) : (
            <div className="no-data-card">
              <Phone size={40} />
              <h3>No local contacts added</h3>
              <p>Add local contacts to this itinerary.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}